import { AggregateStatusEnumOptions } from "@boundless/bl-api-client/partners/models/AggregateStatusEnumLabels"
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table"
import DatePicker from "react-datepicker"

import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { fundingRequestColumns } from "@/lib/data-tables/funding-request-columns"
import { cn } from "@/lib/utils"

import type { FundingRequest } from "@boundless/bl-api-client/partners/models"

export function PartnerReferralDetailsPopover({
  fundingRequests,
}: {
  fundingRequests: FundingRequest[]
}) {
  const table = useReactTable<FundingRequest>({
    data: fundingRequests,
    columns: fundingRequestColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  const getDateFilter = (
    id: string,
  ): { from: Date | null; to: Date | null } => {
    return (
      (table.getColumn(id)?.getFilterValue() as {
        from: Date | null
        to: Date | null
      }) ?? {
        from: null,
        to: null,
      }
    )
  }

  const setDateFilter = (
    id: string,
    updates: Partial<{ from: Date | null; to: Date | null }>,
  ): void => {
    const current = getDateFilter(id)
    table.getColumn(id)?.setFilterValue({
      from: updates.from ?? current.from,
      to: updates.to ?? current.to,
    })
  }

  return (
    <Popover>
      <PopoverTrigger
        className={cn(
          "text-primary",
          fundingRequests.length && "cursor-pointer",
        )}
        disabled={!fundingRequests.length}
      >
        Details ({fundingRequests.length})
      </PopoverTrigger>
      <PopoverContent className="w-full">
        <div className="mb-4 grid grid-cols-6 gap-4">
          <div className="space-y-2">
            <Label className="text-base font-medium">Status</Label>
            <Select
              value={
                table.getColumn("aggregateStatus")?.getFilterValue() as string
              }
              onValueChange={(value) =>
                table.getColumn("aggregateStatus")?.setFilterValue(value)
              }
            >
              <SelectTrigger className="!h-9 w-full">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {AggregateStatusEnumOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value ?? ""}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-2 space-y-2">
            <Label className="text-base font-medium">Created</Label>
            <div className="flex space-x-2">
              <div className="min-w-0 flex-1">
                <DatePicker
                  placeholderText="From"
                  selected={getDateFilter("createdAt").from}
                  onChange={(date) =>
                    setDateFilter("createdAt", { from: date })
                  }
                  dateFormat="yyyy-MM-dd"
                  wrapperClassName="w-full"
                  className="h-9 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-1 text-sm shadow-sm placeholder:text-sm"
                  showYearDropdown
                  dropdownMode="select"
                  enableTabLoop={false}
                />
              </div>
              <div className="min-w-0 flex-1">
                <DatePicker
                  placeholderText="To"
                  selected={getDateFilter("createdAt").to}
                  onChange={(date) => setDateFilter("createdAt", { to: date })}
                  dateFormat="yyyy-MM-dd"
                  wrapperClassName="w-full"
                  className="h-9 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-1 text-sm shadow-sm placeholder:text-sm"
                  showYearDropdown
                  dropdownMode="select"
                  enableTabLoop={false}
                />
              </div>
            </div>
          </div>

          <div className="col-span-2 space-y-2">
            <Label className="text-base font-medium">Last Modified</Label>
            <div className="flex space-x-2">
              <div className="min-w-0 flex-1">
                <DatePicker
                  placeholderText="From"
                  selected={getDateFilter("lastModifiedAt").from}
                  onChange={(date) =>
                    setDateFilter("lastModifiedAt", { from: date })
                  }
                  dateFormat="yyyy-MM-dd"
                  wrapperClassName="!w-full"
                  className="h-9 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-1 text-sm shadow-sm placeholder:text-sm"
                  showYearDropdown
                  dropdownMode="select"
                  enableTabLoop={false}
                />
              </div>
              <div className="min-w-0 flex-1">
                <DatePicker
                  placeholderText="To"
                  selected={getDateFilter("lastModifiedAt").to}
                  onChange={(date) =>
                    setDateFilter("lastModifiedAt", { to: date })
                  }
                  dateFormat="yyyy-MM-dd"
                  wrapperClassName="w-full"
                  className="h-9 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-1 text-sm shadow-sm placeholder:text-sm"
                  showYearDropdown
                  dropdownMode="select"
                  enableTabLoop={false}
                />
              </div>
            </div>
          </div>

          <div className="flex items-end">
            <Button
              variant="outline"
              className="h-9 w-full"
              onClick={() => table.resetColumnFilters()}
            >
              Clear Filters
            </Button>
          </div>
        </div>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} className="w-40">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={fundingRequests.length + 1}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </PopoverContent>
    </Popover>
  )
}
